<template>
  <span>
    <v-btn
      small
      outlined
      color="primary"
      class="text-none mr-2"
      @click="triggerUpload"
    >
      <v-icon left small>mdi-upload</v-icon>
      Upload CSV
    </v-btn>
    <input
      ref="fileInput"
      type="file"
      accept=".csv"
      style="display: none;"
      @change="onFileSelected"
    />

    <v-snackbar v-model="snackbar" :color="snackColor" timeout="4000" top>
      <v-icon left dark small>{{ snackIcon }}</v-icon>
      {{ snackText }}
    </v-snackbar>
  </span>

</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'UploadCsvBtn',
  data() {
    return {
      snackbar: false,
      snackText: '',
      snackColor: 'success',
      snackIcon: 'mdi-check-circle',
    };
  },
  computed: {
    ...mapState('monthlyPlanning', ['parts']),
  },
  methods: {
    ...mapActions('monthlyPlanning', ['submitMonthlyPlan']),
    triggerUpload() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.parseCsv(e.target.result);
      };
      reader.readAsText(file);
    },
    async parseCsv(text) {
      const lines = text.trim().split('\n');
      if (lines.length < 2) {
        this.showSnack('CSV must have header + at least 1 row', 'error');
        return;
      }
      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const records = [];
      const invalidParts = [];
      for (let i = 1; i < lines.length; i += 1) {
        const values = this.parseCsvLine(lines[i]);
        if (values.length < headers.length) {
          // skip invalid row
        } else {
          const row = {};
          headers.forEach((h, idx) => {
            row[h] = values[idx];
          });
          if (!row.partname || !row.quantity || !row.monthyear) {
            // skip incomplete row
          } else {
            const qty = Number(row.quantity) || 0;
            // Validate partname against part master
            const matchedPart = this.parts && this.parts.length
              ? this.parts.find(
                (p) => p.partname === row.partname
                  && (!row.color || p.color === row.color
                    || p.colorname === row.color),
              ) || this.parts.find((p) => p.partname === row.partname)
              : null;
            if (!matchedPart) {
              invalidParts.push(row.partname);
            } else {
              let wt = parseFloat(row.netweight || '0') || 0;
              if (!wt) {
                wt = parseFloat(
                  matchedPart.netweight || matchedPart.weight
                    || matchedPart.netwt || '0',
                ) || 0;
              }
              const tonnage = qty && wt
                ? parseFloat(((qty * wt) / 1000).toFixed(3))
                : 0;
              const assetId = this.$store.state.helper.selectedAssetId
                || this.$store.state.helper.assetId
                || 0;
              records.push({
                partname: row.partname,
                color: row.color || matchedPart.color
                  || matchedPart.colorname || '',
                quantity: qty,
                netweight: row.netweight || String(wt) || '',
                productionintonnage: tonnage,
                monthyear: row.monthyear
                  ? parseInt(`${row.monthyear.replace(/-/g, '').substring(0, 6)}01`, 10)
                  : 0,
                machinename: row.machinename || '',
                assetid: assetId,
              });
            }
          }
        }
      }
      if (invalidParts.length) {
        this.showSnack(
          `Invalid parts not in master: ${invalidParts.join(', ')}`,
          'error',
        );
        return;
      }
      if (!records.length) {
        this.showSnack('No valid records found in CSV', 'error');
        return;
      }
      // Consolidate duplicates: sum qty for same part+color+month
      const consolidated = [];
      const map = {};
      records.forEach((r) => {
        const key = `${r.partname}||${r.color}||${r.monthyear}`;
        if (!map[key]) {
          map[key] = { ...r };
          consolidated.push(map[key]);
        } else {
          map[key].quantity += r.quantity;
          const wt = parseFloat(map[key].netweight) || 0;
          map[key].productionintonnage = wt
            ? parseFloat(((map[key].quantity * wt) / 1000).toFixed(3))
            : map[key].productionintonnage + r.productionintonnage;
          if (r.machinename && !map[key].machinename) {
            map[key].machinename = r.machinename;
          }
        }
      });
      try {
        const result = await this.submitMonthlyPlan(consolidated);
        if (result && result.success) {
          this.showSnack(
            `${records.length} records uploaded successfully`,
            'success',
          );
          this.$emit('uploaded');
        } else {
          this.showSnack(
            result ? result.error : 'Failed to upload records',
            'error',
          );
        }
      } catch (e) {
        this.showSnack(e.message || 'Failed to upload records', 'error');
      }
    },
    showSnack(text, type) {
      this.snackText = text;
      this.snackColor = type === 'error' ? 'error' : 'success';
      this.snackIcon = type === 'error'
        ? 'mdi-alert-circle' : 'mdi-check-circle';
      this.snackbar = true;
    },
    parseCsvLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      result.push(current.trim());
      return result;
    },
  },
};
</script>
